import every from "lodash/every";
import extend from "lodash/extend";
import { useEffect } from "react";
import { useState } from "react";
import { useLocation, useSearchParams } from "react-router";

type MsTeamsInitParams = {
  force?: boolean;
};

export const isMsTeams = () => {
  const isIframe = window.self !== window.parent;
  const ancestorOrigins = document.location.ancestorOrigins?.[0] || "";
  const isMsTeamsReferrer =
    ancestorOrigins.indexOf("teams.microsoft.com") > -1 ||
    ancestorOrigins.indexOf("teams.cloud.microsoft") > -1;
  const isFirefox = navigator.userAgent.indexOf("Firefox") > -1; // Firefox does not support ancestorOrigins

  return isIframe && (isMsTeamsReferrer || isFirefox);
};

export const openChatInMsTeams = (emails: string) => {
  const chatUrl = `https://teams.microsoft.com/l/chat/0/0?users=${emails}`;
  return isMsTeams() && !!window.microsoftTeams.executeDeepLink(chatUrl);
};

const STORAGE_KEY = "ms_teams_domain";

export const applyTeamsDomain = () => {
  const domain = window.storage.getItem(STORAGE_KEY);
  if (domain) {
    extend(window.PF.config, { ms_teams_domain: domain });
  }
};

export const setTeamsDomain = (domain: string) => window.storage.setItem(STORAGE_KEY, domain);

export const initializeMsTeams = (params: MsTeamsInitParams = {}) => {
  if (params.force || isMsTeams()) {
    applyTeamsDomain();

    return import("@microsoft/teams-js").then((microsoftTeams) => {
      microsoftTeams.initialize();
      window.microsoftTeams = microsoftTeams;
      return Promise.resolve(microsoftTeams);
    });
  } else {
    applyTeamsDomain();
    return Promise.resolve();
  }
};

export const useInitializeMsTeams = () => {
  const [searchParams] = useSearchParams();

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const initializeMsTeamsFnc = (params: MsTeamsInitParams) =>
      initializeMsTeams(params).then(() => setInitialized(true));
    const forceInitializeMsTeam =
      searchParams.get("msTeams") && searchParams.get("oauthRedirectMethod") !== "deeplink";
    initializeMsTeamsFnc({ force: !!forceInitializeMsTeam });
  }, []);

  return initialized;
};

export const useStoreMsTeamsLastVisitedPath = () => {
  const location = useLocation();
  useEffect(() => {
    const blocklistedPaths = ["/sign_out", "/ms_teams", "/onboarding"];
    if (isMsTeams() && every(blocklistedPaths, (path) => (location.pathname || "").indexOf(path) === -1)) {
      window.storage.setItem("ms_teams_last_visited_path", location.pathname);
    }
  }, [location.pathname]);
};
